import React, { useRef } from 'react';

interface ZipFileUploadProps {
  selectedFile: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}

export const ZipFileUpload: React.FC<ZipFileUploadProps> = ({
  selectedFile,
  onChange,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    
    if (file && !file.name.endsWith('.zip')) {
      alert('Please select a .zip file');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onChange(null);
      return;
    }
    
    onChange(file);
  };

  const handleClear = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-2">
      <label htmlFor="zip-file" className="block text-sm font-medium text-gray-700">
        3DVista Project (ZIP File) <span className="text-red-500">*</span>
      </label>
      
      <div className="mt-1 flex items-center gap-3">
        <label className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            id="zip-file"
            accept=".zip"
            onChange={handleFileChange}
            disabled={disabled}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100
              disabled:opacity-50 disabled:cursor-not-allowed"
            required
          />
        </label>
        
        {selectedFile && (
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="px-3 py-2 text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            Clear
          </button>
        )}
      </div>

      {selectedFile && (
        <div className="mt-2 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-700">
            <span className="font-medium">Selected:</span> {selectedFile.name}
          </p>
          <p className="text-sm text-gray-500">
            Size: {formatFileSize(selectedFile.size)}
          </p>
        </div>
      )}

      <p className="text-sm text-gray-500">
        Export your 3DVista project and upload the entire folder as a .zip file
      </p>
    </div>
  );
};

